// Which interactions reference an element or a scene (pure). Used to tell the
// user exactly what a deletion affects and to build the EXPLICIT resolution
// the user confirms — never to repair a document behind their back (the
// Creative Engine still validates whatever is left).

import type { Action, CreativeDocument, Expr, Rule, Scene } from "@interactads/creative-model";

export interface Removed {
  readonly elements?: readonly string[];
  readonly scene?: string;
  /** Forms removed with their last field (7.6E). */
  readonly forms?: readonly string[];
  /** The survey instrument removed (7.6E). */
  readonly survey?: string;
}

const hits = (removed: Removed, id: string | undefined, kind: "element" | "scene") =>
  id !== undefined && (kind === "element" ? (removed.elements?.includes(id) ?? false) : removed.scene === id);

function triggerHits(rule: Rule, removed: Removed): boolean {
  const on = rule.on as Record<string, unknown>;
  if (typeof on.target === "string" && hits(removed, on.target, "element")) return true;
  if (typeof on.onto === "string" && hits(removed, on.onto, "element")) return true;
  if (typeof on.scene === "string" && hits(removed, on.scene, "scene")) return true;
  if (typeof on.form === "string" && (removed.forms?.includes(on.form) ?? false)) return true;
  if (typeof on.survey === "string" && removed.survey === on.survey) return true;
  for (const side of [on.a, on.b])
    if (side && typeof side === "object" && hits(removed, (side as { id?: string }).id, "element")) return true;
  return false;
}

function exprHits(e: Expr | undefined, removed: Removed): boolean {
  if (e === undefined || typeof e !== "object") return false;
  if ("elementState" in e) return hits(removed, e.elementState, "element");
  if ("field" in e) return removed.forms?.includes(e.form) ?? false;
  if ("answered" in e) return removed.survey !== undefined;
  if ("args" in e) return e.args.some((a) => exprHits(a, removed));
  if ("arg" in e) return exprHits(e.arg, removed);
  return false;
}

function actionHits(a: Action, removed: Removed): boolean {
  const r = a as Record<string, unknown>;
  if (typeof r.target === "string" && hits(removed, r.target, "element")) return true;
  if (typeof r.template === "string" && hits(removed, r.template, "element")) return true;
  if (typeof r.scene === "string" && hits(removed, r.scene, "scene")) return true;
  if (a.type === "despawn" && hits(removed, a.target.id, "element")) return true;
  if (typeof r.form === "string" && (removed.forms?.includes(r.form) ?? false)) return true;
  if (typeof r.survey === "string" && removed.survey === r.survey) return true;
  return false;
}

/** Drops actions aimed at removed things (recursively through `after`); returns null when nothing is left. */
function pruneActions(actions: readonly Action[], removed: Removed): Action[] | null {
  const out: Action[] = [];
  for (const a of actions) {
    if (a.type === "after") {
      const inner = pruneActions(a.do, removed);
      if (inner && inner.length > 0) out.push(inner.length === a.do.length ? a : { ...a, do: inner });
      continue;
    }
    if (!actionHits(a, removed)) out.push(a);
  }
  return out.length === 0 ? null : out;
}

const actionsEqual = (a: readonly Action[], b: readonly Action[]) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The rule after removing elements/a scene: the same object when unaffected,
 * null when it must go (triggered by, or conditioned on, what is removed, or
 * left without actions), or a copy without the actions aimed at it.
 */
export function pruneRule(rule: Rule, removed: Removed): Rule | null {
  if (triggerHits(rule, removed) || exprHits(rule.if, removed)) return null;
  const actions = pruneActions(rule.do, removed);
  if (actions === null) return null;
  return actionsEqual(actions, rule.do) ? rule : { ...rule, do: actions };
}

/** Everything that points at a scene, for the delete-scene dialog. */
export interface SceneReferences {
  readonly rules: readonly Rule[];
  /** Scenes whose "at the end of the timeline" goes to this scene. */
  readonly endLinks: readonly Scene[];
  readonly isInitial: boolean;
  /** Blocking: modules the Studio does not author yet (survey presentation, generated fallback). */
  readonly blocking: readonly string[];
}

export function sceneReferences(doc: CreativeDocument, sceneId: string): SceneReferences {
  const scene = doc.scenes.find((s) => s.id === sceneId);
  const elements = scene
    ? [...new Set([...scene.children, ...Object.values(scene.childrenByLayout ?? {}).flat()])]
    : [];
  const removed: Removed = { scene: sceneId, elements };
  const blocking: string[] = [];
  const p = doc.survey?.presentation;
  if (p && [p.sceneId, p.completionSceneId, p.exitSceneId].includes(sceneId)) blocking.push("la encuesta");
  if (doc.survey?.consent.sceneId === sceneId) blocking.push("el consentimiento de la encuesta");
  // The only scene where the game is played (7.6F): remove the game first.
  if (doc.game?.scenes.length === 1 && doc.game.scenes[0] === sceneId) blocking.push("el juego");
  if (Object.values(doc.fallback?.perLayout ?? {}).some((f) => f.generatedFrom?.sceneId === sceneId))
    blocking.push("la imagen de respaldo generada");
  return {
    rules: (doc.rules ?? []).filter((r) => pruneRule(r, removed) !== r),
    endLinks: doc.scenes.filter(
      (s) => s.id !== sceneId && typeof s.onTimelineEnd === "object" && s.onTimelineEnd.goTo === sceneId
    ),
    isInitial: doc.initialSceneId === sceneId,
    blocking,
  };
}
